import { useApi } from "@/hooks";
import ConfirmDialog from "@Components/admin/confirm-dialog";
import { ScenarioEditDialog } from "@Components/game/scenario-editor";
import { buildScenarioRecoveryId, clearScenarioRecoveryDraft } from "@Containers/game/builder/functions/recovery-storage";
import { saveScenario } from "@Containers/game/builder/functions/scenario-functions";
import { Events, UnityBridge } from "@Context/common/general/context";
import { useNotificationDialog } from "@Context/common/general/notification-dialog-context";
import { useBuilderContext } from "@Context/game/builder/builder-context";
import { useGlobalContext } from "@hooks/index";
import useI18n from "@i18n";
import { Close } from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  MenuItem,
  Select,
  Slider,
  Stack,
  Switch,
  Typography,
  useTheme,
} from "@mui/material";
import "@pixi/events";
import { Api } from "@Shared/schemas";
import { ScenarioResponse } from "@Shared/schemas/scenarios/scenarioSchemas";
import { ScenarioData } from "@Shared/types/ScenarioTypes";
import { isEmpty } from "lodash-es";
import { useEffect, useState } from "react";
import ScenarioImporter from "./importers/scenario-importer";

const BuilderMenu = ({
  scenarioMetadata,
  setScenarioMetadata,
}: {
  scenarioMetadata?: ScenarioResponse;
  setScenarioMetadata: (metadata: ScenarioResponse) => void;
}) => {
  const { state, dispatch } = useBuilderContext();
  const { showNotification } = useNotificationDialog();
  const { i18n } = useI18n();
  const { globalState } = useGlobalContext();
  const isDeveloper = globalState.user?.isSuperAdmin;
  const theme = useTheme();
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [showInfoDialog, setShowInfoDialog] = useState<boolean>(false);
  const [exportedScenario, setExportedScenario] = useState<ScenarioData | undefined>();
  const [showExitConfirmDialog, setShowExitConfirmDialog] = useState<boolean>(false);

  const {
    call: getScenario,
    data: getScenarioResponse,
    loading: getScenarioLoading,
  } = useApi<typeof Api.Scenarios.GetScenario>({
    api: Api.Scenarios.GetScenario,
    request: {
      scenario_id: scenarioMetadata?.doc_id || "",
      is_staging: true,
    },
    onSuccess: response => {
      if (response?.data?.metadata) {
        setScenarioMetadata(response.data.metadata);
        clearScenarioRecoveryDraft(buildScenarioRecoveryId(response.data.metadata));
      }
    },
  });

  const {
    call: updateScenario,
    data: updateScenarioResponse,
    loading: updateScenarioLoading,
  } = useApi<typeof Api.Scenarios.UpdateScenario>({
    api: Api.Scenarios.UpdateScenario,
    request: {
      scenario_id: "",
      scenario_data: null,
    },
    onSuccess: () => {
      showNotification({
        type: "success",
        title: i18n("builder.menu.save.success.title"),
        message: i18n("builder.menu.save.success.message"),
      });
      clearScenarioRecoveryDraft(buildScenarioRecoveryId(scenarioMetadata));
      dispatch({ clip: { ...state.clip, isDirty: false } });
    },
    onError: () => {
      showNotification({
        type: "error",
        title: i18n("builder.menu.save.error.title"),
        message: i18n("builder.menu.save.error.message"),
      });
    },
  });

  const canSaveExistingScenario = !!scenarioMetadata && !isEmpty(scenarioMetadata.doc_id);

  const handleExport = (fileName: string) => {
    const data = saveScenario(state.clip, state.field);
    const element = document.createElement("a");
    const textFile = new Blob([JSON.stringify(data)], { type: "text/plain" }); //pass data from localStorage API to blob
    element.href = URL.createObjectURL(textFile);
    element.download = fileName;
    document.body.appendChild(element);
    element.click();
  };

  const handleSaveClick = () => {
    if (!canSaveExistingScenario) return;
    updateScenario({
      scenario_id: scenarioMetadata?.doc_id || "",
      scenario_data: saveScenario(state.clip, state.field),
    });
  };

  const handleSaveAsClick = () => {
    // open scenario info modal
    setExportedScenario(saveScenario(state.clip, state.field));
    setShowInfoDialog(true);
    // dispatch({ isDirty: false });
  };

  const handleBallBehaviourChange = (behaviour: "tracking" | "actions") => {
    const newClip = { ...state.clip };
    if (behaviour === "tracking") {
      if (!isEmpty(state.clip.scenario.ball.disabledPoints)) {
        newClip.scenario.ball.points = [...state.clip.scenario.ball.disabledPoints];
        newClip.scenario.ball.disabledPoints = [];
        newClip.isDirty = true;
      }
    } else {
      if (!isEmpty(state.clip.scenario.ball.points)) {
        newClip.scenario.ball.disabledPoints = [...state.clip.scenario.ball.points];
        newClip.scenario.ball.points = [];
        newClip.isDirty = true;
      }
    }
    if (newClip.isDirty) {
      dispatch({ clip: newClip });
    }
  };

  const handleSaveAsComplete = (scenarioId: number) => {
    console.log("Save as complete", scenarioId);
    clearScenarioRecoveryDraft(buildScenarioRecoveryId(scenarioMetadata));
    dispatch({ clip: { ...state.clip, isDirty: false } });
    getScenario({ scenario_id: scenarioId.toString(), is_staging: true });
    setShowInfoDialog(false);
  };

  const handleExitClicked = () => {
    console.log("Exiting builder");
    if (state.clip.isDirty) {
      setShowExitConfirmDialog(true);
    } else {
      Events.app.navigateBack.emit();
    }
  };

  const handleConfirmExitClicked = () => {
    setShowExitConfirmDialog(false);
    Events.app.navigateBack.emit();
  };

  const handlePlayTest = () => {
    const scenarioData = saveScenario(state.clip, state.field);
    if (!scenarioData) return;

    UnityBridge.dispatchWebEvent({
      type: UnityBridge.OutgoingWebEvents.PlayTest,
      payload: {
        scenario: scenarioData,
        game_speed: state.options.gameSpeed,
      },
    });
  };

  useEffect(() => {
    console.log("View rotation changed", state.viewRotation);
  }, [state.viewRotation]);

  return (
    <>
      {state.showBuilderMenu && (
        <Box
          onClick={() => dispatch({ showBuilderMenu: false })}
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: theme.zIndex.drawer - 1,
            backgroundColor: "transparent",
          }}
        />
      )}
      <Drawer anchor="left" variant="persistent" open={state.showBuilderMenu} onClose={() => dispatch({ showBuilderMenu: false })}>
        <IconButton onClick={() => dispatch({ showBuilderMenu: false })} size="small" sx={{ position: "absolute", top: 28, right: 16 }}>
          <Close />
        </IconButton>
        <Stack sx={{ p: 4 }} spacing={1.5}>
          <Typography
            variant="h5"
            sx={{
              color: theme.palette.dsBase.v200,
              paddingBottom: theme.spacing(1),
              maxWidth: "250px",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {scenarioMetadata?.name || i18n("builder.menu.untitled")}
          </Typography>
          {/* <Button variant='outlined' fullWidth disabled onClick={() => { setShowInfoDialog(true) }}>Edit Info</Button>
            <Divider /> */}
          <Button
            variant={state.clip.isDirty ? "contained" : "outlined"}
            disabled={!canSaveExistingScenario || updateScenarioLoading}
            fullWidth
            onClick={handleSaveClick}
          >
            {updateScenarioLoading ? <CircularProgress size={24} /> : i18n("builder.menu.save")}
          </Button>
          <Button variant="outlined" fullWidth disabled={updateScenarioLoading} onClick={handleSaveAsClick}>
            {updateScenarioLoading ? <CircularProgress size={24} /> : i18n("builder.menu.save.as")}
          </Button>
          <Divider />
          <Button variant="outlined" fullWidth onClick={handlePlayTest}>
            {i18n("builder.menu.play.test")}
          </Button>
          <Divider />
          <Button
            variant="outlined"
            fullWidth
            onClick={() => {
              dispatch({ showImportDialog: true });
            }}
          >
            {i18n("builder.menu.import.file")}
          </Button>
          <Button variant="outlined" fullWidth onClick={() => handleExport(state.clip.scenario.fileName)}>
            {i18n("builder.menu.export.file")}
          </Button>
          <Divider />
          <Button variant="outlined" fullWidth onClick={handleExitClicked}>
            {i18n("builder.menu.exit")}
          </Button>
          <Typography variant="h5" sx={{ color: theme.palette.dsBase.v200, paddingTop: theme.spacing(4), paddingBottom: theme.spacing(1) }}>
            {i18n("builder.menu.settings")}
          </Typography>
          {isDeveloper && (
            <Stack spacing={1}>
              <Typography color={theme.palette.dsBase.v200}>Ball Behaviour:</Typography>
              <Select
                value={isEmpty(state.clip.scenario.ball.points) ? "actions" : "tracking"}
                onChange={e => handleBallBehaviourChange(e.target.value as "tracking" | "actions")}
              >
                <MenuItem value="tracking">Follow Tracking</MenuItem>
                <MenuItem value="actions">Use Actions</MenuItem>
              </Select>
            </Stack>
          )}
          <Stack spacing={1}>
            <Typography color={theme.palette.dsBase.v200}>{i18n("builder.menu.view.rotation")}</Typography>
            <Slider value={state.viewRotation} onChange={(e, v) => dispatch({ viewRotation: v })} min={0} max={1.5 * Math.PI} step={0.5 * Math.PI} />
          </Stack>
          <Stack spacing={1}>
            <Typography color={theme.palette.dsBase.v200}>{i18n("builder.menu.play.speed")}</Typography>
            <Slider
              valueLabelDisplay="auto"
              valueLabelFormat={value => <div>{Math.round(value * 100)}%</div>}
              value={state.options.gameSpeed}
              onChange={(e, v) => dispatch({ options: { ...state.options, gameSpeed: v } })}
              min={0.5}
              max={2}
              step={0.05}
            />
          </Stack>
          <Stack spacing={1}>
            <Typography color={theme.palette.dsBase.v200}>{i18n("builder.menu.play.resolution")}</Typography>
            <Slider
              valueLabelDisplay="auto"
              valueLabelFormat={value => <div>Every {Math.round(value * 1000) / 1000}s</div>}
              value={state.options.playbackResolution}
              onChange={(e, v) => dispatch({ options: { ...state.options, playbackResolution: v } })}
              min={0.01}
              max={0.1}
              step={0.005}
            />
          </Stack>
          <Stack spacing={1}>
            <Typography color={theme.palette.dsBase.v200}>{i18n("builder.menu.player.speed.limit")}</Typography>
            <Slider
              valueLabelDisplay="auto"
              valueLabelFormat={value => <div>{value.toFixed(1)}m/s</div>}
              value={state.options.playerSpeedLimit}
              onChange={(e, v) => dispatch({ options: { ...state.options, playerSpeedLimit: v } })}
              min={0.5}
              max={50}
              step={0.5}
            />
          </Stack>
          <Stack spacing={1}>
            <Typography color={theme.palette.dsBase.v200}>{i18n("builder.menu.ball.speed.limit")}</Typography>
            <Slider
              valueLabelDisplay="auto"
              valueLabelFormat={value => <div>{value.toFixed(1)}m/s</div>}
              value={state.options.ballSpeedLimit}
              onChange={(e, v) => dispatch({ options: { ...state.options, ballSpeedLimit: v } })}
              min={0.5}
              max={100}
              step={0.5}
            />
          </Stack>
          <Divider />
          <Stack direction={"row"} spacing={0} alignItems={"center"}>
            <Switch
              checked={state.options.showWaypoints}
              onChange={() => dispatch({ options: { ...state.options, showWaypoints: !state.options.showWaypoints } })}
            />
            <Typography color={theme.palette.dsBase.v200}>{i18n("builder.menu.show.waypoints")}</Typography>
          </Stack>
          <Stack direction={"row"} spacing={0} alignItems={"center"}>
            <Switch
              checked={state.options.showActions}
              onChange={() => dispatch({ options: { ...state.options, showActions: !state.options.showActions } })}
            />
            <Typography color={theme.palette.dsBase.v200}>{i18n("builder.menu.show.actions")}</Typography>
          </Stack>
          <Stack direction={"row"} spacing={0} alignItems={"center"}>
            <Switch
              checked={state.options.showBallPath}
              onChange={() => dispatch({ options: { ...state.options, showBallPath: !state.options.showBallPath } })}
            />
            <Typography color={theme.palette.dsBase.v200}>{i18n("builder.menu.show.ball.path")}</Typography>
          </Stack>
          <Stack direction={"row"} spacing={0} alignItems={"center"}>
            <Switch
              checked={state.options.showJerseyNumbers}
              onChange={() => dispatch({ options: { ...state.options, showJerseyNumbers: !state.options.showJerseyNumbers } })}
            />
            <Typography color={theme.palette.dsBase.v200}>{i18n("builder.menu.show.jersey.numbers")}</Typography>
          </Stack>
          <Stack spacing={1}>
            <Typography color={theme.palette.dsBase.v200}>{i18n("builder.menu.route.fade.distance")}</Typography>
            <Slider
              value={state.options.pathLength}
              onChange={(e, v) => dispatch({ options: { ...state.options, pathLength: v } })}
              min={0}
              max={20}
              step={1}
            />
          </Stack>
        </Stack>
      </Drawer>
      <ScenarioEditDialog
        open={showInfoDialog}
        scenarioEditorData={exportedScenario}
        title={i18n("builder.menu.save.as")}
        buttonLabel={i18n("builder.menu.save")}
        onClose={() => setShowInfoDialog(false)}
        onCreate={handleSaveAsComplete}
      />
      <ScenarioImporter onError={setErrorMessage} />
      {showExitConfirmDialog && (
        <ConfirmDialog
          open={showExitConfirmDialog}
          confirm={s => handleConfirmExitClicked()}
          cancel={() => setShowExitConfirmDialog(false)}
          title={i18n("builder.menu.exit.confirm.title")}
          message={i18n("builder.menu.exit.confirm.message")}
          icon={undefined}
        />
      )}
    </>
  );
};

export default BuilderMenu;
